import { useState, useCallback } from 'react'
import { RoleRow, roleRowService } from '../model/RoleRow'
import { useRoleRepository } from '../../../../services/adaptors/roleRepositoryAdaptor'
import {
  RoleEntity,
  CreateRoleInput,
  UpdateRoleInput,
  SaveRoleOrderInput,
} from '../../../../applications/ports/roleRepository'
import store from '../../../../store'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import _ from 'lodash'
import { PermissionPolicyOption } from '../../../../domain/value-object/PermissionPolicy'

type UseRoleDataReturn = {
  data: RoleRow[]
  loading: boolean
  hasChanged: () => boolean
  fetchRoles: () => Promise<void>
  saveRoles: () => Promise<void>
  addRow: (attachedByDefaultPolicies: PermissionPolicyOption[]) => void
  deleteRows: (uuids: string[]) => void
  setDataAfterEditing: (data: RoleRow[]) => void
}

export const useRoleData = (functionUuid: string): UseRoleDataReturn => {
  const [data, setData] = useState<RoleRow[]>([])
  const [originalData, setOriginalData] = useState<RoleRow[]>([])
  const [loading, setLoading] = useState(false)
  const repository = useRoleRepository()

  const hasChanged = useCallback(() => {
    return (
      originalData.length !== data.length ||
      data.some(
        (row, index) =>
          row.added || row.edited || row.uuid !== originalData[index].uuid
      )
    )
  }, [data, originalData])

  const fetchRoles = useCallback(async () => {
    setLoading(true)
    try {
      const roles: RoleEntity[] = await repository.getList()
      const rows: RoleRow[] = roles.map(role =>
        roleRowService.fromRoleEntity(role)
      )
      setData(rows)
      setOriginalData(_.cloneDeep(rows))
    } finally {
      setLoading(false)
    }
  }, [repository])

  const saveRoles = useCallback(async () => {
    setLoading(true)
    try {
      // 変更前の行データ
      const originalDataMap: Record<string, RoleRow> = {}
      originalData.forEach(v => {
        originalDataMap[v.uuid] = v
      })

      // Validate rows in a single loop
      const deletedUuids = originalData
        .filter(original => !data.some(row => row.uuid === original.uuid))
        .map(original => original.uuid)

      for (const row of data) {
        const original = originalDataMap[row.uuid]

        // Check if non-editable role is edited
        if (original && !original.editable && row.edited) {
          store.dispatch(
            addScreenMessage(functionUuid, {
              type: MessageLevel.ERROR,
              title: intl.formatMessage(
                { id: 'roles.validation.cannotEditNonEditableRole' },
                { code: row.code, name: row.name }
              ),
            })
          )
          return
        }
      }

      // Check deleted rows
      for (const deletedUuid of deletedUuids) {
        const original = originalDataMap[deletedUuid]

        // Check if non-editable role is deleted
        if (!original.editable) {
          store.dispatch(
            addScreenMessage(functionUuid, {
              type: MessageLevel.ERROR,
              title: intl.formatMessage(
                { id: 'roles.validation.cannotEditNonEditableRole' },
                { code: original.code, name: original.name }
              ),
            })
          )
          return
        }

        // Check if in-use role is deleted
        if (original.inUse) {
          store.dispatch(
            addScreenMessage(functionUuid, {
              type: MessageLevel.ERROR,
              title: intl.formatMessage(
                { id: 'roles.validation.cannotDeleteInUseRole' },
                { code: original.code, name: original.name }
              ),
            })
          )
          return
        }
      }

      const added: CreateRoleInput[] = []
      const edited: UpdateRoleInput[] = []
      const order: SaveRoleOrderInput = {
        roleOrder: [],
      }
      data.forEach((row, index) => {
        order.roleOrder.push({ roleUuid: row.uuid, displayOrder: index + 1 })
        if (row.added) {
          added.push(roleRowService.toCreateRoleInput(row, index + 1))
          return
        }
        const originalData = originalDataMap[row.uuid]
        if (row.edited && !row.added && originalData) {
          edited.push(
            roleRowService.toUpdateRoleInput(
              row,
              originalDataMap[row.uuid],
              index + 1
            )
          )
          return
        }
      })

      await repository.batchUpdate({
        added,
        edited,
        deleted: deletedUuids.map(uuid => ({ uuid })),
        order,
      })

      store.dispatch(
        addScreenMessage(functionUuid, {
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage({ id: 'registration.complete' }),
        })
      )

      await fetchRoles()
    } finally {
      setLoading(false)
    }
  }, [data, originalData, repository, functionUuid, fetchRoles])

  const addRow = useCallback(
    (attachedByDefaultPolicies: PermissionPolicyOption[]) => {
      const newRow: RoleRow = roleRowService.create(
        data.map(v => v.code),
        attachedByDefaultPolicies.map(v => v.uuid),
        data.length + 1
      )
      setData([...data, newRow])
    },
    [data]
  )

  const deleteRows = useCallback(
    (uuids: string[]) => {
      setData(data.filter(row => !uuids.includes(row.uuid)))
    },
    [data]
  )

  const setDataAfterEditing = useCallback((newData: RoleRow[]) => {
    setData(newData)
  }, [])

  return {
    data,
    loading,
    hasChanged,
    fetchRoles,
    saveRoles,
    addRow,
    deleteRows,
    setDataAfterEditing,
  }
}
